<template>
  <div class="reporting__time" :class="{frequency: isFrequency}">
    <div class="title-container">
      <div class="title-wrap">
        <h3>
          Hourly PV Status
        </h3>
        <ui-icon-tooltip class="tooltip-icon" :iconSize="'normal'" arrowBottom arrowRight>
          <div>{{ $t('common.timezone') }} : ({{ applyData.timezone }}) {{ applyData.timezoneName }}</div>
        </ui-icon-tooltip>
      </div>
      <div class="desc">
        {{ $t('reporting.timeSeries.title') }}
      </div>
    </div>
    <div class="chart-container" v-if="timeSeries && timeSeries.length > 0">
      <ul class="chart-y">
        <li class="max-text">
          <span>(PV)</span>
          {{ maxPv }}
        </li>
        <li>{{ maxPv / 2 }}</li>
        <li>0</li>
      </ul>
      <div class="graph-container" :style="{height: viewPortWidth > 1603 ? containerHeight(maxTopPv) + '%' : '100%'}">
        <template v-for="(data, index) in timeSeries">
          <div class="chart-x"
               :class="[index === topPv && !showTooltip && !timeseriesData ? 'topPv' : '', `level-${getHeatmapBackground(data[0])}`, index === timeseriesData && !showTooltip ? 'onClick' : '']"
               :style="{pointerEvents: resizing ? 'none' : 'auto'}"
               :key="index"
               @click="getDeviceDataByHour(index)"
               @mouseenter="showTooltip = true"
               @mouseleave="showTooltip = false">
              <div class="graph-wrap">
                <div class="graph" :style="`clip-path: inset(calc(100% - ${calculateHeight(data[0])}%) 0 0 0);`">
                </div>
                <div class="graph-bg" :style="{height: index === topPv || showTooltip || index === timeseriesData ? calculateHeight(data[0]) === 100 ? '100%' : calculateHeight(data[0]) + 1 + '%' : calculateHeight(data[0]) + '%'}">
                  <div class="tooltip" :class="[index === timeseriesData && !showTooltip ? 'on' : '', getTooltipPosition(index)]">
                    <div class="tooltip-time">{{ getTooltipTimeSeries(index) }}</div>
                    <div class="tooltip-pv">{{ data[0] }} PV</div>
                  </div>
                </div>
              </div>
            <click-animation class="tooltip-click"
                             :class="[getTooltipPosition(index)]" />
            <span class="time"
                  :class="[index % 2 !== 0 ? 'odd' : '', timeseriesData === index && timeseriesData ? 'onClick' : '']"
                  :data-title="getTimeSeries(index)">
            </span>
            <div class="tooltip" :class="[timeseriesData === index && !showTooltip ? 'on' : '', getTooltipPosition(index)]" v-if="viewPortWidth < 1604">
              <div class="tooltip-time">{{ getTooltipTimeSeries(index) }}</div>
              <div class="tooltip-pv">{{ data[0] }} PV</div>
            </div>
          </div>
        </template>
      </div>
    </div>
    <div class="none-pv-container" v-else>
      <div class="none-pv-text" v-if="viewPortWidth > 1603">
        {{ $t('reporting.timeSeries.noPvByDate') }}
      </div>
      <div class="none-pv-text two-line" v-else>
        {{ $t('reporting.timeSeries.noPvByDateBr') }}
      </div>
    </div>
  </div>
</template>

<script>
import UiIconTooltip from '@/components/ui/UiIconTooltip.vue'
import { mapMutations, mapState } from 'vuex'
import ClickAnimation from '@/components/common/ClickAnimation.vue'

export default {
  name: 'ReportSegment',
  components: {
    ClickAnimation,
    UiIconTooltip
  },
  data () {
    return {
      showTooltip: false,
      containerWidth: 0,
      maxPv: null,
      maxTopPv: null,
      viewPortWidth: 0,
      resizing: false
    }
  },
  props: {
    timeSeries: {
      type: Array,
      default: () => []
    },
    infoData: {
      type: Object,
      default: () => {}
    },
    isFrequency: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapState([
      'applyData',
      'reportPeriod',
      'timeseriesData'
    ]),
    topPv () {
      return this.getTopPv()
    }
  },
  methods: {
    ...mapMutations([
      'setTimeseriesData'
    ]),
    /**
     * PV 값에 따른 그래프 높이 계산
     * @param {number} value
     * @returns {number}
     */
    calculateHeight (value) {
      const height = Math.floor((value / this.maxTopPv) * 100)
      if (this.viewPortWidth > 1603) {
        return height
      }
      return height
    },
    containerHeight (value) {
      const height = Math.floor((value / this.maxPv) * 100)
      return height
    },
    /**
     * container width 변경 시, containerWidth 업데이트
     * @returns {void}
     */
    updateContainerWidth () {
      this.containerWidth = this.$el.getBoundingClientRect().width
    },
    /**
     * 최대 PV 값 조회
     * @returns {number}
     */
    getTopPv () {
      const pvData = [...this.timeSeries]
      // PV 값으로 내림차순 정렬하고, 같은 PV 값을 가지면 시간으로 내림차순 정렬
      if (pvData) {
        pvData.sort((a, b) => b[0] - a[0] || b[1] - a[1])
        this.maxPv = Math.ceil(pvData[0][0] / 10) * 10
        this.maxTopPv = pvData[0][0]

        // 최대 PV 값과 동일하고, 가장 최근 시간을 가진 요소의 인덱스 찾기
        return this.timeSeries.findIndex((item) => item[0] === pvData[0][0] && item[1] === pvData[0][1])
      }
    },
    /**
     * 시간대별 PV 데이터 조회
     * @param value
     */
    getDeviceDataByHour (value) {
      if (this.timeseriesData === value) {
        this.$emit('onDeviceDataByHour', '')
        this.$store.commit('clearTimeseriesData')
      } else {
        this.setTimeseriesData(value)
        this.$emit('onDeviceDataByHour', value)
      }
    },
    /**
     * heatmap 배경색 계산
     * @param {number} value
     * @returns {number}
     */
    getHeatmapBackground (value) {
      const unit = this.maxTopPv / 20
      const item = Math.floor(value / unit) + 1
      return item > 20 ? 20 : item
    },
    /**
     * 툴팁의 시간대 계산
     * @param {string} params
     * @returns {string}
     */
    getTooltipTimeSeries (params) {
      if (params.toString().length === 1) {
        return '0' + params + ':00 ~ ' + '0' + params + ':59'
      } else {
        return params + ':00 ~ ' + params + ':59'
      }
    },
    /**
     * 히트맵 시간대 AM, PM 표시
     * @param {string} params
     * @returns {string}
     */
    getTimeSeries (params) {
      if (params.toString().length === 1) {
        if (this.viewPortWidth < 1603) {
          if (params === 0) {
            return `AM \n 0${params}`
          }
        }
        return '0' + params
      } else {
        if (this.viewPortWidth < 1603) {
          if (params === 12) {
            return `PM \n ${params}`
          }
          return params
        }
      }
      return params
    },
    /**
     * 툴팁 위치 계산
     * @param {number} params
     * @returns {string}
     */
    getTooltipPosition (params) {
      if (this.viewPortWidth < 1603) {
        if (params <= 11) {
          return 'tooltipBottom'
        } else {
          return 'tooltipTop'
        }
      } else {
        if (params <= 11) {
          return 'tooltipRight'
        } else {
          return 'tooltipLeft'
        }
      }
    },
    getViewPortWidth () {
      this.viewPortWidth = window.innerWidth
    }
  },
  mounted () {
    this.updateContainerWidth()
    this.getViewPortWidth()
    window.addEventListener('resize', () => {
      this.resizing = true
      clearTimeout(this.resizeTimer)
      this.getViewPortWidth()
      this.resizeTimer = setTimeout(() => {
        this.resizing = false
      }, 100)
    })
  },
  beforeDestroy () {
    window.removeEventListener('resize', () => {
      this.resizing = false
      this.getViewPortWidth()
    })
  },
  destroyed () {
    this.$emit('onActiveSelectorChange', null)
  }
}
</script>

<style lang="scss" scoped>
.reporting__time {
  background-color: #fff;
  padding: 25px;
  border-radius: 14px;
  margin-top: 30px;
  margin-bottom: 50px;
  container-name: heatmap;
  container-type: inline-size;
  box-shadow: 0px 23px 15px -12px rgba(0, 0, 0, 0.08), 0px 0px 0px 1px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  gap: 30px;
  font-family: Roboto, 'Noto Sans KR', sans-serif;
  height: 307px;

  &.frequency {
    margin-bottom: 0;
  }

  .title-container {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .title-wrap {
      display: flex;
      justify-content: space-between;

      h3 {
        font-size: 15px;
        font-weight: 550;
        color: #2f2f2f;
        display: flex;
        align-items: center;
        gap: 8px;

        > .icon.icon-beta {
          width: 37px;
          height: 100%;
          background-image: url('../../assets/img/icon_beta.svg');
          background-position-y: center;
          background-repeat: no-repeat;
          background-size: contain;
        }
      }
    }
    .desc {
      font-size: 12px;
      color: #767676;
    }
  }

  .chart-container {
    display: flex;
    justify-content: center;
    align-items: flex-end;
    gap: 1em;
    position: relative;
    margin-bottom: 25px;
    height: 100%;

    .chart-y {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: center;
      margin-right: 1rem;
      color: #444;
      font-size: 12px;
      height: 100%;

      .max-text {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 5px;

        span {
          font-size: 10px;
          color: #767676;
        }
      }
    }

    .graph-container {
      width: 100%;
      display: flex;
      gap: 2px;
      flex-basis: auto;

      .chart-x {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-end;
        gap: 1.3rem;
        position: relative;
        cursor: pointer;

        .tooltip-click {
          width: 90px;
          height: 70px;
          position: absolute;
          top: -50px;
          z-index: 2;
          display: none;
          gap: 2px;
          pointer-events: none;
        }

        .graph-wrap {
          width: 100%;
          height: 100%;
          position: relative;
          bottom: 0;
          left: 0;
          display: flex;
          align-items: center;
          transition: transform .3s;
          transform-origin: bottom center;
          .graph {
            background-image: linear-gradient(180deg, #EC0048 24.5%, #FA570E 41%, #FFC700 56%, #EFE610 68.5%, #C0DF03 78%, #3ECF1B 86%, #04A078 92%, #006CB9 97%, #0F1F2B 100%);
            width: 100%;
            height: 100%;
            transition: box-shadow .3s;
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
          }
          .graph-bg {
            width: 100%;
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
          }
        }

        .time {
          position: absolute;
          top: 100%;
          padding: 5px 0;
          margin-top: 0;
          width: 100%;
          text-align: center;
          font-family: Roboto, 'Noto Sans KR', sans-serif;

          &::before {
            top: 0;
            left: 50%;
            font-family: Roboto, 'Noto Sans KR', sans-serif;
            font-weight: 400;
            font-size: 12px;
            color: #444;
            content: attr(data-title);
            transform: translateX(-50%);
          }

          &.onClick {
            &::before {
              color: #222;
              font-weight: 550;
            }
          }
        }

        .odd {
          &::before {
            color: #aaa;
          }
        }

        .tooltip {
          min-width: 112px;
          min-height: 63px;
          position: absolute;
          top: 0;
          z-index: 9999;
          border-radius: 8px;
          box-shadow: 0px 8px 15px 0px rgba(0, 0, 0, 0.16);
          outline: 1px solid rgba(0, 0, 0, 0.16);
          background-color: #ffffff;
          padding: 15px;
          box-sizing: border-box;
          opacity: 1;
          display: none;
          pointer-events: none;

          div {
            width: 100%;
            font-weight: 550;
          }

          .tooltip-time {
            font-weight: 550;
            font-size: 13px;
            color: #444
          }

          .tooltip-pv {
            font-weight: 550;
            color: #ec0047;
          }

          &.on {
            display: flex;
            flex-direction: column;
            gap: 8px;

            &.tooltipRight {
              top: 0;
              right: 0;
              transform: translate(105%, -50%);
            }

            &.tooltipLeft {
              top: 0;
              left: 0;
              transform: translate(-105%, -50%);
            }
          }
        }

        &.onClick {
          z-index: 2;
          .graph-wrap {
            width: 110%;
            z-index: 2;
            .graph {
              box-shadow: 0 0 0 2px inset #fff;
            }
            .graph-bg {
              background-clip: border-box;
              background-color: #fff;
              z-index: -3;
              box-shadow: 0 0 0 2px #222;
            }
          }

          .time {
            &::before {
              color: #EC0047;
              font-weight: 550;
            }
          }
        }

        &.topPv {
          z-index: 2;

          .graph-wrap {
            width: 120%;
            transform: scale(1.1);
            z-index: 2;
            .graph {
              box-shadow: 0 0 0 2px inset #fff;
            }
            .graph-bg {
              background-clip: border-box;
              background-color: #fff;
              z-index: -3;
              box-shadow: 0px 4px 10px 0px #00000080;
            }
          }

          .time::before {
            font-size: 14px;
            font-weight: 550;
            color: #222;
          }

          .tooltip-click {
            display: block;
          }
        }

        &:hover {
          z-index: 3;
          position: relative;

          .graph-wrap {
            z-index: 2;
            width: 120%;
            transform: scale(1.1);
            .graph {
              box-shadow: 0 0 0 2px inset #fff;
            }
            .graph-bg {
              background-clip: border-box;
              background-color: #fff;
              z-index: -3;
              box-shadow: 0 4px 10px rgba(0, 0, 0, 0.5);
            }
          }

          .tooltip {
            position: absolute;
            display: flex;
            flex-direction: column;
            gap: 8px;
            z-index: 5;

            &.tooltipRight {
              top: 0;
              right: 0;
              transform: translate(102%, -50%);
            }

            &.tooltipLeft {
              top: 0;
              left: 0;
              transform: translate(-102%, -50%);
            }
          }

          .time::before {
            color: #222;
            font-weight: 550;
            font-size: 14px;
          }
        }
      }
    }
  }

  .none-pv-container {
    height: 180px;
    display: flex;
    align-items: center;
    justify-content: center;

    .none-pv-text {
      text-align: center;
      color: #2f2f2f;
      font-size: 13px;
      font-weight: 400;

      &.two-line {
        white-space: pre-wrap;
        line-height: 1.5;
      }
    }
  }
}

//@container heatmap (max-width: 559px) {
@media all and (max-width: 1604px) {
  .reporting__time {
    gap: 21px;

    .chart-container {
      width: 100%;
      max-width: 468px;
      max-height: 190px;
      align-items: initial;
      margin: 0 auto;

      .chart-y {
        display: none;
      }

      .graph-container {
        max-height: 190px;
        display: grid;
        grid-template-columns: repeat(6, 1fr);

        .chart-x {
          height: 100%;
          transition: border .3s, box-shadow .3s, transform .3s;
          transform-origin: center;
          cursor: pointer;
          border: 2px solid transparent;
          justify-content: center;

          .graph {
            display: none;
          }

          &.onClick {
            z-index: 2;
            box-shadow: 0 0 0 2px inset #fff;
            border: 2px solid #222;
            transform: scale(1.1);
            position: relative;

            .graph-wrap {
              display: none;
            }
          }

          .time {
            width: 30px;
            text-align: center;
            top: 50%;
            transform: translateY(-50%);
            height: fit-content;

            &::before {
              font-size: 14px;
              color: #fff;
            }

            &.onClick {
              &::before {
                color: #fff;
                font-weight: 600;
              }
            }
          }

          .tooltip-click {
            top: -40px;
          }

          &.topPv {
            z-index: 2;
            border: 2px solid #fff;
            box-shadow: 0 4px 10px rgba(0, 0, 0, 0.5);
            transform: scale(1.1);

            .graph {
              display: none;
            }

            .graph-wrap {
              display: none;
            }
          }

          .tooltip {
            &.tooltipTop {
              bottom: 100%;
              transform: translateY(-108%);
            }

            &.tooltipBottom {
              left: 0;
              transform: translate(-15%, 79%);
            }
          }

          &.level-7, &.level-8, &.level-9, &.level-10, &.level-11, &.level-12, &.level-13,
          &.level-14, &.level-15, &.level-16, &.level-17, &.level-18, &.level-19, &.level-20 {
            .time {
              &::before {
                color: #fff;
              }
            }
          }

          &.level-1, &.level-2, &.level-3, &.level-4, &.level-5, &.level-6, {
            .time {
              &::before {
                color: rgba(0, 0, 0, 0.7);
              }
            }
          }

          &:hover {
            z-index: 3;
            border: 2px solid #fff;
            box-shadow: 0 4px 10px rgba(0, 0, 0, 0.5);
            transform: scale(1.1);

            .graph-wrap {
              display: none;
            }

            .tooltip {
              position: absolute;
              display: flex;
              flex-direction: column;
              gap: 8px;

              &.tooltipTop {
                bottom: 100%;
                transform: translateY(-108%);
              }

              &.tooltipBottom {
                z-index: 99999;
                left: 0;
                transform: translate(-15%, 79%);
              }

            }

            &.level-7, &.level-8, &.level-9, &.level-10, &.level-11, &.level-12, &.level-13,
            &.level-14, &.level-15, &.level-16, &.level-17, &.level-18, &.level-19, &.level-20 {
              .time::before {
                color: #fff;
                font-weight: 600;
              }
            }

            &.level-1, &.level-2, &.level-3, &.level-4, &.level-5 &.level-6 {
              .time::before {
                color: rgba(0, 0, 0, 0.7);
                font-weight: 600;
              }
            }

          }
        }
      }

    }
  }

  .level-20 {
    background-color: #EC0149;
  }

  .level-19 {
    background-color: #EE0C50;
  }

  .level-18 {
    background-color: #EF1556;
  }

  .level-18 {
    background-color: #EE1454;
  }

  .level-17 {
    background-color: #EF1C59;
  }

  .level-16 {
    background-color: #F0235D;
  }

  .level-15 {
    background-color: #F12A62;
  }

  .level-14 {
    background-color: #F4396D;
  }

  .level-13 {
    background-color: #F54273;
  }

  .level-12 {
    background-color: #F74A78;
  }

  .level-11 {
    background-color: #F8537E;
  }

  .level-10 {
    background-color: #F96086;
  }

  .level-9 {
    background-color: #FA6F91;
  }

  .level-8 {
    background-color: #FA7E9C;
  }

  .level-7 {
    background-color: #FB8DA7;
  }

  .level-6 {
    background-color: #FC9CB1;
  }

  .level-5 {
    background-color: #FDABBC;
  }

  .level-4 {
    background-color: #FDBAC7;
  }

  .level-3 {
    background-color: #FEC9D2;
  }

  .level-2 {
    background-color: #FFD7DC;
  }

  .level-1 {
    background-color: #FFE8E8;
  }
}

</style>
